import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getImageUrl } from "../lib/utils";
import { useTranslation } from "../i18n/LanguageContext";

const HeroCarousel = ({ slides }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [slides.length, nextSlide]);

  if (!slides || slides.length === 0) {
    return (
      <section className="min-h-[85vh] flex items-center justify-center bg-[#0B1121]">
        <p className="text-slate-400">{t("loading")}</p>
      </section>
    );
  }

  const currentSlide = slides[currentIndex];

  return (
    <section id="home" className="relative min-h-[85vh] overflow-hidden" data-testid="hero-carousel">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${getImageUrl(slide.image_url)})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1121] via-[#0B1121]/80 to-transparent" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full min-h-[85vh] flex items-center">
        <div className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4 opacity-0 animate-slide-up animate-delay-100"
             style={{ animationFillMode: 'forwards' }}>
            {currentSlide.subtitle}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 opacity-0 animate-slide-up animate-delay-200"
              style={{ animationFillMode: 'forwards' }}>
            <span className="gradient-text">{currentSlide.title}</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed opacity-0 animate-slide-up animate-delay-300"
             style={{ animationFillMode: 'forwards' }}>
            {currentSlide.description}
          </p>
          <a
            href={currentSlide.cta_link}
            className="inline-block px-8 py-4 btn-primary rounded-full text-lg opacity-0 animate-slide-up animate-delay-400"
            style={{ animationFillMode: 'forwards' }}
            data-testid="hero-cta-button"
          >
            {currentSlide.cta_text}
          </a>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-[#2395E3]/30 transition-all" data-testid="carousel-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-[#2395E3]/30 transition-all" data-testid="carousel-next">
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {slides.map((_, index) => (
              <button key={index} onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${index === currentIndex ? "bg-[#00CCC2] w-8" : "bg-white/30 hover:bg-white/50"}`}
                data-testid={`carousel-dot-${index}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroCarousel;
